import type { StudyTool } from './studyToolTypes';
import { STUDY_TOOLS } from './studyTools';
import { registryAllowsYear, registryHidesTool, type StudyToolRegistry } from './studyToolRegistry';
import { subjectAvailability, studyToolHidden, type StudyAvailability } from './studyAvailability';
import { studyToolsOn, type StudyToolAccess } from './studyApi';
import { STUDY_TOOL_TOPICS, isRecommendationPreview } from './studyToolTopicCatalog';

export interface LearningToolContext {
  subjectKey: string;
  text: string;
  label: string;
  returnTo: string;
  sourceVersion?: number;
}
/** Topic data is complete for the registered catalog; admission is checked independently. */
const RULES = STUDY_TOOL_TOPICS;

export function contextualTools(
  context: LearningToolContext,
  policy: {
    registry?: StudyToolRegistry;
    availability?: StudyAvailability;
    access?: StudyToolAccess;
    year: number | null;
  },
): { tool: StudyTool; reason: string }[] {
  // Recommendations are optional: policy uncertainty must not advertise unavailable capability.
  if (!policy.registry || !policy.availability || !policy.access || policy.year === null) return [];
  const registry = policy.registry;
  const subject = subjectAvailability(policy.availability, context.subjectKey);
  if (
    !policy.availability.study_visible ||
    !subject?.enabled ||
    !subject.tools_enabled ||
    subject.tutor.state === 'exam_window' ||
    !studyToolsOn(policy.access, context.subjectKey)
  )
    return [];
  const text = context.text.slice(0, 16000).normalize('NFKC').toLocaleLowerCase();
  // Junior learners often file all three sciences under Science. Keep that source gate,
  // then admit each target subject independently; a topic match is never permission.
  const targets =
    context.subjectKey === 'science'
      ? ['science', 'physics', 'chemistry', 'biology']
      : [context.subjectKey];
  return RULES.flatMap((rule) => {
    const tool = STUDY_TOOLS.find(
      (item) => item.id === rule.id && targets.includes(item.subjectKey),
    );
    const target = tool ? subjectAvailability(policy.availability, tool.subjectKey) : null;
    if (
      !tool ||
      !target?.enabled ||
      !target.tools_enabled ||
      target.tutor.state === 'exam_window' ||
      !studyToolsOn(policy.access, tool.subjectKey) ||
      isRecommendationPreview(tool) ||
      tool.path === context.returnTo.split('?')[0] ||
      !rule.terms.test(text) ||
      studyToolHidden(policy.availability, tool.id) ||
      registryHidesTool(registry, tool.id) ||
      !registryAllowsYear(registry, tool, policy.year)
    )
      return [];
    const entry = registry.tools.find((item) => item.tool_key === tool.id);
    if (entry?.status === 'preview') return [];
    const matches = text.match(new RegExp(rule.terms.source, 'gi')) ?? [];
    return [
      { tool, reason: rule.reason, score: Math.max(...matches.map((match) => match.length)) },
    ];
  })
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map(({ tool, reason }) => ({ tool, reason }));
}

interface Handoff extends LearningToolContext {
  owner: string;
  toolPath: string;
  createdAt: number;
}
const handoffs = new Map<string, Handoff>();
export function learningToolExcerpt(text: string) {
  if (text.length <= 500) return text;
  const prefix = text.slice(0, 500);
  const sentence = Math.max(
    prefix.lastIndexOf('. '),
    prefix.lastIndexOf('。'),
    prefix.lastIndexOf('\n'),
  );
  const boundary = sentence >= 300 ? sentence + 1 : prefix.lastIndexOf(' ');
  return `${prefix.slice(0, boundary >= 250 ? boundary : 499).trimEnd()}…`;
}
export function rememberLearningTool(
  owner: string,
  toolPath: string,
  context: LearningToolContext,
) {
  const key = crypto.randomUUID();
  if (!/^\/learn\/study(?:[/?]|$)/.test(context.returnTo) || context.returnTo.length > 1800)
    return null;
  // Nothing is persisted or sent. A reload/account switch cannot recover another learner's text.
  for (const [id, value] of handoffs)
    if (value.owner !== owner || Date.now() - value.createdAt > 3600000) handoffs.delete(id);
  if (handoffs.size >= 20) handoffs.delete(handoffs.keys().next().value!);
  handoffs.set(key, {
    ...context,
    text: learningToolExcerpt(context.text),
    label: context.label.slice(0, 120),
    owner,
    toolPath,
    createdAt: Date.now(),
  });
  return key;
}
export function readLearningTool(key: string, owner: string, toolPath: string) {
  const value = handoffs.get(key);
  return value &&
    value.owner === owner &&
    value.toolPath === toolPath &&
    Date.now() - value.createdAt < 3600000
    ? value
    : null;
}
